import { supabase } from '../../lib/supabase'

// ============================================================
// THE ONLY WAY THE BROWSER CAUSES AN EMAIL
//
// `send-invite` takes an event name and an entity id. It does not take an
// address, a name, a subject, a message or an `extra` bag — those were the
// hole: `send-notification` accepted all five from any signed-in caller and
// mailed them as "Lentals <notifications@lentals.com>", and for the five
// invite types minted a Supabase magic link for whatever address the caller
// named.
//
// So a call site's job is now to say WHICH EVENT and WHICH ROW. The server
// reads the recipient, their name, the token, the property address and every
// word of the body out of the database (migration 394), and mints the magic
// link for the address the database returned.
//
// WHY THESE TWELVE AND NOT THE DIGEST. Every one can have a recipient with no
// Lentals account — live: 19 of 20 prospects, 16 of 19 tenant_invites, 4 of 6
// approved_contractors, every pending team member and client invite — and
// `notifications.user_id` is NOT NULL and FKs to `profiles.id`. With no
// in-app row there is nothing for `send-digest-notifications` to find, so the
// ============================================================

export type InviteEvent =
  // invites — these mint a magic link
  | 'prospect_invite'
  | 'tenant_invite'
  | 'landlord_client_invited'
  | 'contractor_invited'
  | 'team_member_invited'
  // prospect pipeline — no link; the prospect already holds theirs
  | 'viewing_scheduled'
  | 'prospect_application'
  | 'referencing_started'
  | 'prospect_declined'
  | 'prospect_converted'
  // same shape, not an invite
  | 'contractor_removed'

export interface SendInviteResult {
  success: boolean
  /** Set when the send was refused. 'rate_limited' is distinguishable so a UI can say so. */
  error?: string
  rateLimited?: boolean
}

/**
 * Ask the server to send one of the twelve events for one entity row.
 *
 * `entityId` is the event's own entity, and the server decides whether the
 * caller may act on it:
 *
 *   prospect_invite / prospect_application / prospect_declined /
 *   prospect_converted        -> prospects.id
 *   viewing_scheduled         -> prospect_viewings.id
 *   referencing_started       -> prospect_referencing.id
 *   tenant_invite             -> tenant_invites.id
 *   team_member_invited       -> team_members.id
 *   landlord_client_invited   -> landlord_client_users.id
 *   contractor_invited /
 *   contractor_removed        -> approved_contractors.id
 */
export async function sendInvite(
  event: InviteEvent,
  entityId: string
): Promise<SendInviteResult> {
  try {
    const { data, error } = await supabase.functions.invoke('send-invite', {
      body: { event, entity_id: entityId },
    })

    if (error) {
      // A 429 arrives as a FunctionsHttpError; surface it distinguishably so a
      // caller can say "too many emails" rather than "something went wrong".
      const status = (error as { context?: { status?: number } }).context?.status
      if (status === 429) {
        return { success: false, rateLimited: true, error: 'Too many emails sent recently. Try again shortly.' }
      }
      console.error(`[sendInvite] ${event} failed:`, error.message)
      return { success: false, error: 'Failed to send email. Please try again.' }
    }

    if (data?.error) {
      console.error(`[sendInvite] ${event} refused:`, data.error)
      return { success: false, error: data.error }
    }

    return { success: true }
  } catch (err) {
    console.error(`[sendInvite] ${event} threw:`, err)
    return { success: false, error: 'Failed to send email. Please try again.' }
  }
}
